import { uploadAttachment } from '@/components/ap/media/attachmentUpload';
import { AppConfig } from '@/config/appConfig';
import { updateConfigHeaderForDemoUseStagingAI } from '@/lib/commonHelpers';
import { message, notification } from '@/lib/notify/apNotify';
import { useAuthStore } from '@/store/authStore';
import { useDesmosStore } from '@/store/desmosStore';
import useSTTTokenStore from '@/store/sttTokenStore';
import axios from 'axios';
import { toPng } from 'html-to-image';
import i18n from 'i18next';

// Wire + size/type constants live in the antd-free attachmentUpload module (shared with v4).
export { MAX_IMAGE_SIZE, SUPPORTED_IMAGE_TYPES } from '@/components/ap/media/attachmentUpload';

// Store timeout ID for clearing
const translate = i18n.t;

/**
 * Handles image upload to the backend — v3 wrapper over the shared wire that
 * surfaces failures via antd message (v4 uses its own sonner wrapper).
 * @param file - The file to upload
 * @param onProgress - Optional callback to receive upload progress (0-100)
 */
export const handleImageUpload = async (
  file: File,
  onProgress?: (percentage: number) => void
): Promise<string> => {
  try {
    return await uploadAttachment(file, onProgress);
  } catch (error: any) {
    console.error('Upload error:', error);
    message.error(
      translate('APEditorUtils.file_upload_failed_please_try_again', { error: error.message })
    );
    throw error;
  }
};

/**
 * Converts file to base64 for preview
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Upload image from data URL to the backend and return the uploaded image URL
 */
export const imageInsertFromDataUrl = async (dataUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const token = useAuthStore.getState().token;

    if (!token) {
      useDesmosStore.getState().setShowLoading(false);
      reject(new Error('Authentication token not available'));
      return;
    }

    const url = `${AppConfig.apiUrl}/attachments`;
    const formData = new FormData();
    formData.append('url', dataUrl);

    fetch(url, {
      method: 'POST',
      headers: {
        token: token,
      },
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data && data.url) {
          resolve(data.url);
        } else {
          throw new Error('Invalid response from server');
        }
        useDesmosStore.getState().setShowLoading(false);
      })
      .catch((error) => {
        console.log('Something went wrong in saving calc canvas image ==>', error);
        notification.error({
          message: 'Something went wrong in copying from calculator.',
          key: 'copy-to-apeditor',
        });
        useDesmosStore.getState().setShowLoading(false);
        reject(error);
      });
  });
};

/**
 * Copy Desmos calculator content to APEditor
 * Similar to copyToCK but adapted for APEditor
 */
export const copyToAPEditor = {
  onCopyToAPEditor: async (editor: any) => {
    if (!editor) {
      notification.warning({
        message: 'Editor is not available',
        key: 'copy-to-apeditor-warning',
      });
      return;
    }
    //setting blur to the element to avoid the issue of the calculator input bg color showing when the image is inserted and the focus is on the input of calculator.
    const focusedElement = document.getElementsByClassName('dcg-focus-visible')[0] as HTMLElement;
    focusedElement?.blur();
    const scientificCalcClass = 'dcg-basic-list';
    const scientificEle = document.getElementsByClassName(scientificCalcClass)[0] as HTMLElement;
    // v3's graphing gate matched the wrapper class 'graphing-calculator'; the
    // v4 tool window hosts Desmos under this testid instead.
    const graphingEle = document.querySelector<HTMLElement>('[data-testid="desmos-graphing-host"]');
    try {
      if (scientificEle) {
        useDesmosStore.getState().setShowLoading(true);
        //hide the cursor element of the calculator
        const dataUrl = await toPng(scientificEle, {
          width: scientificEle?.offsetWidth,
          height: scientificEle?.offsetHeight,
          pixelRatio: 1,
          filter: (node: Node) => {
            return !(node instanceof Element && node.classList.contains('dcg-mq-cursor'));
          },
        });
        const imageUrl = await imageInsertFromDataUrl(dataUrl);

        // Insert image into APEditor
        editor.chain().focus().setCustomImage({ src: imageUrl }).run();
      } else if (graphingEle) {
        useDesmosStore.getState().setShowLoading(true);
        const dataUrl = await toPng(graphingEle, {
          width: graphingEle?.offsetWidth,
          height: graphingEle?.offsetHeight,
          pixelRatio: 1,
        });
        const imageUrl = await imageInsertFromDataUrl(dataUrl);

        // Insert image into APEditor
        editor.chain().focus().setCustomImage({ src: imageUrl }).run();
      } else {
        notification.warning({
          message: 'Your calculator must be visible',
          key: 'copy-to-apeditor-warning',
        });
      }
    } catch (error) {
      console.error('Error in copyToAPEditor:', error);
      notification.error({
        message: 'Failed to copy calculator content',
        key: 'copy-to-apeditor-error',
      });
      useDesmosStore.getState().setShowLoading(false);
    }
  },
};

/**
 * Tool configuration for APEditor calculator functionality
 */
export const APEditorCalcTool = {
  toolbarName: 'copyToAPEditor',
  copyToAPEditor: copyToAPEditor,
};

export const fetchValidSTTToken = async () => {
  const { sttToken, sttTokenExpirationTime, setSTTToken } = useSTTTokenStore.getState();
  const currentTime = new Date().getTime();
  const speechToTextAPIEndpoint = `/stt/token`;
  const baseUrl = AppConfig.aiServiceUrlOld;
  // Check if token exists and is not expired
  if (sttToken && sttTokenExpirationTime && currentTime < sttTokenExpirationTime) {
    return { authToken: sttToken, region: 'westeurope', error: null };
  }

  const token = useAuthStore.getState().token;

  try {
    let config = {
      method: 'POST',
      url: `${baseUrl}${speechToTextAPIEndpoint}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      meta: { dataProviderName: 'aiService' },
    };

    config = updateConfigHeaderForDemoUseStagingAI(config);
    const response = await axios.request(config);

    if (response.status === 200) {
      const newToken = response.data.token;
      const tokenValidityDuration = 540 * 1000; // Token expiration time in milliseconds (9 minutes)
      const newExpirationTime = currentTime + tokenValidityDuration;

      setSTTToken(newToken, newExpirationTime);

      // Schedule token refresh
      setTimeout(fetchValidSTTToken, tokenValidityDuration);

      return { authToken: newToken, region: 'westeurope', error: null };
    } else {
      console.error('Unexpected response status:', response.status);
      return { authToken: '', region: '', error: 'Unexpected response status' };
    }
  } catch (err: any) {
    console.error('Error fetching token:', err.response ? err.response.data : err.message);
    return { authToken: '', region: '', error: err.response ? err.response.data : err.message };
  }
};
